import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Mail, LogOut, Save, TrendingUp, Users, Target, Loader2, Heart, Check, X, Clock, Inbox, Trash2, FileClock, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { authApi, fundraisingApi, donationApi, downloadFile, getToken, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';

function AdminPage() {
  const [isAuthed, setIsAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (getToken()) {
      authApi.me()
        .then(() => setIsAuthed(true))
        .catch(() => {
          authApi.logout();
          setIsAuthed(false);
        })
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50">
      <Toaster />
      {isAuthed ? (
        <Dashboard onLogout={() => { authApi.logout(); setIsAuthed(false); }} toast={toast} />
      ) : (
        <LoginForm onSuccess={() => setIsAuthed(true)} toast={toast} />
      )}
    </div>
  );
}

function LoginForm({ onSuccess, toast }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authApi.login(email, password);
      toast({ title: 'Berjaya!', description: 'Selamat datang, Pentadbir KDU' });
      onSuccess();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Log masuk gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-white fill-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Panel Pentadbir KDU</h1>
            <p className="text-gray-500 mt-1">Log masuk untuk kemaskini dana</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">Emel</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sriiduat.edu.my"
                  className="pl-11 h-12 border-2 rounded-xl"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700 mb-2 block">Kata Laluan</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-11 h-12 border-2 rounded-xl"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="admin-login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Log Masuk'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function Dashboard({ onLogout, toast }) {
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ goal: '', current: '', donorsCount: '', currentPhaseGoal: '' });
  const [loading, setLoading] = useState(false);

  const loadStats = () => {
    fundraisingApi.get().then((data) => {
      setStats(data);
      setForm({
        goal: data.goal,
        current: data.current,
        donorsCount: data.donorsCount,
        currentPhaseGoal: data.currentPhaseGoal,
      });
    }).catch(() => {});
  };

  useEffect(() => {
    loadStats();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        goal: parseInt(form.goal, 10),
        current: parseInt(form.current, 10),
        donorsCount: parseInt(form.donorsCount, 10),
        currentPhaseGoal: parseInt(form.currentPhaseGoal, 10),
      };
      const updated = await fundraisingApi.update(payload);
      setStats(updated);
      toast({ title: 'Disimpan!', description: 'Maklumat dana telah dikemaskini' });
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const percentage = stats && stats.goal > 0 ? Math.round((stats.current / stats.goal) * 100) : 0;

  const fields = [
    { key: 'current', label: 'Jumlah Terkumpul (RM)', icon: TrendingUp },
    { key: 'goal', label: 'Sasaran Keseluruhan (RM)', icon: Target },
    { key: 'currentPhaseGoal', label: 'Sasaran Fasa Semasa (RM)', icon: Target },
    { key: 'donorsCount', label: 'Bilangan Penyumbang', icon: Users },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
            <Heart className="w-6 h-6 text-white fill-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Panel Pentadbir</h1>
            <p className="text-sm text-gray-500">Kompleks Darul Ummah (KDU)</p>
          </div>
        </div>
        <Button
          onClick={onLogout}
          variant="outline"
          data-testid="admin-logout-button"
          className="rounded-xl border-2"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Log Keluar
        </Button>
      </div>

      {/* Current progress preview */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-8 text-white mb-8 shadow-xl"
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-purple-100">Progress Semasa</span>
            <span className="text-2xl font-bold" data-testid="admin-percentage">{percentage}%</span>
          </div>
          <div className="h-3 bg-white/20 rounded-full overflow-hidden mb-6">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 1 }}
              className="h-full bg-gradient-to-r from-purple-300 to-pink-300 rounded-full"
            />
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">RM {stats.current.toLocaleString()}</div>
              <div className="text-purple-200 text-xs">Terkumpul</div>
            </div>
            <div>
              <div className="text-2xl font-bold">RM {stats.goal.toLocaleString()}</div>
              <div className="text-purple-200 text-xs">Sasaran</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{stats.donorsCount}</div>
              <div className="text-purple-200 text-xs">Penyumbang</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Edit form */}
      <div className="bg-white rounded-3xl shadow-lg p-8 border border-purple-100">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Kemaskini Maklumat Dana</h2>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map((field) => {
              const Icon = field.icon;
              return (
                <div key={field.key}>
                  <Label className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-purple-600" />
                    {field.label}
                  </Label>
                  <Input
                    type="number"
                    data-testid={`admin-input-${field.key}`}
                    value={form[field.key]}
                    onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                    className="h-12 border-2 rounded-xl"
                    required
                  />
                </div>
              );
            })}
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-save-button"
            className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Simpan Perubahan</>}
          </Button>
        </form>
      </div>

      {/* Donations manager */}
      <DonationsManager toast={toast} onChange={loadStats} />

      {/* Audit log */}
      <AuditLog toast={toast} />
    </div>
  );
}

function AuditLog({ toast }) {
  const [open, setOpen] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAudit = () => {
    setLoading(true);
    donationApi.audit()
      .then((data) => setRecords(Array.isArray(data) ? data : []))
      .catch((err) => {
        setRecords([]);
        toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) || 'Gagal memuatkan log audit.' });
      })
      .finally(() => setLoading(false));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadAudit();
  };

  const [exportingAudit, setExportingAudit] = useState(false);
  const handleExportAudit = async () => {
    setExportingAudit(true);
    try {
      await downloadFile('/donations/export/audit.csv', 'log_audit.csv');
      toast({ title: 'Berjaya dimuat turun', description: 'Log audit telah dieksport.' });
    } catch (err) {
      toast({ title: 'Ralat', description: 'Gagal mengeksport log audit.' });
    } finally {
      setExportingAudit(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg p-8 border border-purple-100 mt-8" data-testid="audit-log">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileClock className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">Log Audit (Infaq Dipadam)</h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportAudit}
            disabled={exportingAudit}
            data-testid="export-audit-csv-button"
            className="rounded-xl border-2 border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            {exportingAudit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            CSV
          </Button>
          <Button
            variant="outline"
            onClick={toggle}
            data-testid="audit-toggle-button"
            className="rounded-xl border-2"
          >
            {open ? 'Sembunyi' : 'Lihat Log'}
          </Button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mt-2">Rekod infaq yang dipadam untuk tujuan rekonsiliasi LHDN.</p>

      {open && (
        <div className="mt-6">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-purple-600 animate-spin mx-auto" /></div>
          ) : records.length === 0 ? (
            <div className="py-8 text-center text-gray-400" data-testid="audit-empty">
              <FileClock className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>Tiada rekod infaq dipadam</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-3 pr-4">Nama</th>
                    <th className="py-3 pr-4">Jumlah</th>
                    <th className="py-3 pr-4">Status Asal</th>
                    <th className="py-3 pr-4">Dipadam Oleh</th>
                    <th className="py-3">Tarikh Padam</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.audit_id} className="border-b border-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-900">{r.donation?.name || '-'}</td>
                      <td className="py-3 pr-4 text-purple-600 font-semibold">RM {(r.donation?.amount || 0).toLocaleString()}</td>
                      <td className="py-3 pr-4 text-gray-600">{r.donation?.status || '-'}</td>
                      <td className="py-3 pr-4 text-gray-600">{r.deleted_by}</td>
                      <td className="py-3 text-gray-500">{new Date(r.deleted_at).toLocaleString('ms-MY')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DonationsManager({ toast, onChange }) {
  const [donations, setDonations] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [exporting, setExporting] = useState(null);

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      if (kind === 'csv') {
        await downloadFile('/donations/export/verified.csv', 'infaq_disahkan.csv');
      } else if (kind === 'pdf') {
        await downloadFile('/donations/export/verified.pdf', 'laporan_infaq.pdf');
      }
      toast({ title: 'Berjaya dimuat turun', description: 'Fail eksport telah dijana.' });
    } catch (err) {
      toast({ title: 'Ralat', description: 'Gagal menjana eksport. Cuba lagi.' });
    } finally {
      setExporting(null);
    }
  };

  const load = (status) => {
    setLoading(true);
    donationApi.list(status)
      .then((data) => setDonations(Array.isArray(data) ? data : []))
      .catch(() => setDonations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleVerify = async (id) => {
    setActioningId(id);
    try {
      const res = await donationApi.verify(id);
      toast({
        title: 'Infaq Disahkan!',
        description: res.receipt_sent ? 'Resit telah dihantar ke emel penyumbang.' : 'Disahkan. (Resit emel gagal dihantar - semak emel penyumbang)',
      });
      load(filter);
      if (onChange) onChange();
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (id) => {
    setActioningId(id);
    try {
      await donationApi.reject(id);
      toast({ title: 'Ditolak', description: 'Infaq telah ditolak.' });
      load(filter);
      if (onChange) onChange();
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (id) => {
    setActioningId(id);
    try {
      await donationApi.remove(id);
      toast({ title: 'Dipadam', description: 'Infaq telah dipadam. Rekod disimpan dalam log audit.' });
      setDeleteTarget(null);
      load(filter);
      if (onChange) onChange();
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setActioningId(null);
    }
  };

  const filters = [
    { key: 'pending', label: 'Menunggu', icon: Clock },
    { key: 'verified', label: 'Disahkan', icon: Check },
    { key: 'rejected', label: 'Ditolak', icon: X },
  ];

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700',
      verified: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    const label = { pending: 'Menunggu', verified: 'Disahkan', rejected: 'Ditolak' };
    return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || ''}`}>{label[status] || status}</span>;
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg p-8 border border-purple-100 mt-8" data-testid="donations-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Inbox className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">Pengurusan Infaq</h2>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExport('csv')}
            disabled={exporting !== null}
            data-testid="export-csv-button"
            className="rounded-xl border-2 border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
            CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
            data-testid="export-pdf-button"
            className="rounded-xl border-2 border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        {filters.map((f) => {
          const Icon = f.icon;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`donations-filter-${f.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filter === f.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto" /></div>
      ) : donations.length === 0 ? (
        <div className="py-12 text-center text-gray-400" data-testid="donations-empty">
          <Inbox className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Tiada infaq dalam kategori ini</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="donations-list">
          {donations.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-4 p-4 border border-gray-100 rounded-2xl hover:border-purple-200 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 truncate">{d.is_anonymous ? 'Penyumbang (Tanpa Nama)' : d.name}</span>
                  {statusBadge(d.status)}
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">{d.method === 'toyyibpay' ? 'Toyyib Pay' : 'Manual'}</span>
                </div>
                <div className="text-sm text-gray-500 truncate">{d.email}{d.message ? ` • "${d.message}"` : ''}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-bold text-purple-600">RM {d.amount.toLocaleString()}</div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {d.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleVerify(d.id)}
                      disabled={actioningId === d.id}
                      data-testid={`verify-donation-${d.id}`}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-lg"
                    >
                      {actioningId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(d.id)}
                      disabled={actioningId === d.id}
                      data-testid={`reject-donation-${d.id}`}
                      className="border-2 border-amber-300 text-amber-600 hover:bg-amber-50 rounded-lg"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(d)}
                  disabled={actioningId === d.id}
                  data-testid={`delete-donation-${d.id}`}
                  className="border-2 border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Padam kekal (buang spam)"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Padam Infaq Ini?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  Anda akan memadam infaq daripada <strong>{deleteTarget.is_anonymous ? 'Penyumbang (Tanpa Nama)' : deleteTarget.name}</strong> berjumlah <strong>RM {deleteTarget.amount?.toLocaleString()}</strong>.
                  {deleteTarget.status === 'verified' && ' Jumlah terkumpul & bilangan penyumbang akan dikurangkan.'}
                  {' '}Rekod akan disimpan dalam log audit untuk rekonsiliasi LHDN.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && handleDelete(deleteTarget.id)}
              data-testid="delete-confirm-button"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {actioningId ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ya, Padam'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AdminPage;
